import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/** Abstand zum Zeiger, damit die Karte nicht unter dem Mauszeiger klebt. */
const GAP = 14;
/** Mindestabstand zum Fensterrand. */
const MARGIN = 8;

export interface CursorPoint {
  x: number;
  y: number;
}

/**
 * Eine Karte, die am Mauszeiger erscheint und garantiert im Fenster bleibt.
 *
 * Sie hängt bewusst per Portal am `body` und positioniert sich `fixed`: läge
 * sie im Bildcontainer, würde sie bei einer Markierung am unteren oder rechten
 * Bildrand aus dem Sichtbereich rutschen – und wäre damit unbedienbar.
 *
 * Erst messen, dann zeigen: bis die eigene Größe bekannt ist, bleibt die Karte
 * unsichtbar, sonst blitzt sie an der falschen Stelle auf.
 */
export function CursorCard({
  at,
  className,
  children,
  onPointerDownOutside,
}: {
  at: CursorPoint;
  className?: string;
  children: React.ReactNode;
  onPointerDownOutside?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [placed, setPlaced] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const place = () => {
      const element = ref.current;
      if (!element) return;

      const { width, height } = element.getBoundingClientRect();
      const maxLeft = window.innerWidth - width - MARGIN;
      const maxTop = window.innerHeight - height - MARGIN;

      // Bevorzugt rechts unterhalb des Zeigers. Ist dort kein Platz, kippt die
      // Karte auf die andere Seite, statt über den Fensterrand hinauszulaufen.
      let left = at.x + GAP;
      if (left > maxLeft) left = at.x - GAP - width;
      let top = at.y + GAP;
      if (top > maxTop) top = at.y - GAP - height;

      setPlaced({
        left: Math.min(Math.max(MARGIN, left), Math.max(MARGIN, maxLeft)),
        top: Math.min(Math.max(MARGIN, top), Math.max(MARGIN, maxTop)),
      });
    };

    place();

    // Die Karte wächst, sobald eine Person gewählt ist (Notizfeld verschwindet)
    // – dann muss sie neu ausgerichtet werden.
    const observer = new ResizeObserver(place);
    if (ref.current) observer.observe(ref.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [at.x, at.y]);

  useLayoutEffect(() => {
    if (!onPointerDownOutside) return;

    const handler = (event: PointerEvent) => {
      const element = ref.current;
      if (!element) return;
      const target = event.target as Node | null;
      if (target && element.contains(target)) return;
      // Klicks in einem Auswahlmenü (eigenes Portal) gehören noch zur Karte.
      if (target instanceof Element && target.closest('.fb-pop')) return;
      onPointerDownOutside();
    };

    window.addEventListener('pointerdown', handler, true);
    return () => window.removeEventListener('pointerdown', handler, true);
  }, [onPointerDownOutside]);

  return createPortal(
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left: placed?.left ?? at.x,
        top: placed?.top ?? at.y,
        visibility: placed ? 'visible' : 'hidden',
      }}
      // Über dem Vollbild (z-50), aber unter den Auswahlmenüs (z-60).
      className={cn('z-[55]', className)}
    >
      {children}
    </div>,
    document.body,
  );
}
